import { useEffect, useRef } from 'react';
import { drawConsultantThinkingNucleus } from '../utils/consultantThinkingNucleus';

interface ConsultantThinkingNucleusProps { isActive: boolean }

export function ConsultantThinkingNucleus({ isActive }: ConsultantThinkingNucleusProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    // Non-rendering environments such as jsdom do not provide a canvas context.
    if (!canvas || !isActive || typeof CanvasRenderingContext2D === 'undefined') return;
    const context = canvas.getContext('2d');
    if (!context) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const colorScheme = window.matchMedia('(prefers-color-scheme: dark)');
    let isVisible = true;
    let disposed = false;
    let timer: number | undefined;
    let frame: number | undefined;
    let width = 0;
    let height = 0;
    let elapsed = 2;
    let previousTime = 0;

    const stop = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      timer = undefined;
      frame = undefined;
      previousTime = 0;
    };
    const canDraw = () => !disposed && isVisible && !document.hidden && width > 0 && height > 0;
    const draw = () => {
      if (!canDraw()) return;
      const color = window.getComputedStyle(canvas).color;
      drawConsultantThinkingNucleus(context, width, height, elapsed, color);
    };
    const schedule = () => {
      if (!canDraw() || reducedMotion.matches || timer !== undefined || frame !== undefined) return;
      // The timeout both caps the rate and avoids recursion with synchronous rAF test shims.
      timer = window.setTimeout(() => {
        timer = undefined;
        if (!canDraw()) return;
        frame = window.requestAnimationFrame((now) => {
          frame = undefined;
          if (!canDraw()) return;
          if (previousTime) elapsed += Math.min(now - previousTime, 100) * 0.001 * 1.5;
          previousTime = now;
          draw();
          schedule();
        });
      }, 1000 / 30);
    };
    const resume = () => {
      stop();
      draw();
      schedule();
    };
    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = bounds.width;
      height = bounds.height;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      resume();
    };

    const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(resize);
    const intersectionObserver = typeof IntersectionObserver === 'undefined' ? undefined : new IntersectionObserver(([entry]) => {
      isVisible = entry?.isIntersecting ?? false;
      resume();
    });
    const themeObserver = new MutationObserver(resume);
    resizeObserver?.observe(canvas);
    intersectionObserver?.observe(canvas);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style', 'data-theme'] });
    if (document.body) themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'style', 'data-theme'] });
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', resume);
    reducedMotion.addEventListener('change', resume);
    colorScheme.addEventListener('change', resume);
    resize();

    return () => {
      disposed = true;
      stop();
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      themeObserver.disconnect();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', resume);
      reducedMotion.removeEventListener('change', resume);
      colorScheme.removeEventListener('change', resume);
    };
  }, [isActive]);

  return <div className="pm-nucleus" aria-hidden="true"><canvas ref={canvasRef} className="pm-nucleus-canvas" /></div>;
}
